// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/chat/ChatStates.tsx
// 🔧 PURPOSE: Gli stati della chat ospite fuori dalla conversazione
//             (mockup approvato "Chat · stati"):
//             - caricamento: tre bolle segnaposto al posto della rotella;
//             - vuota: il benvenuto con i testi della card Reception della Home;
//             - errore: una card della famiglia con RIPROVA;
//             - sessione scaduta: la chat resta aperta e chiede di accedere;
//             - offline: l'avviso ambra sotto la testata;
//             - "Connesso come …", ora in cima alla conversazione.
// ============================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ArrowDown, Loader2, Lock, LogIn, RotateCw, UserRound, WifiOff } from 'lucide-react';
import { cn } from '../../lib/utils';
import { SERVICE_BUTTON } from '../../pages/services/ServicePageLayout';
import { CHAT_EMBLEM_SRC } from './ChatHeader';

/** "Connesso come Giulia": chi scrive, in piccolo. */
export const ConnectedAs: React.FC<{ name: string; className?: string }> = ({ name, className }) => {
  const { t } = useTranslation();
  return (
    <p className={cn('m-0 inline-flex items-center gap-1.5 self-center text-[12px] text-nva-muted dark:text-stone-400', className)}>
      <UserRound className="h-[13px] w-[13px] flex-shrink-0" strokeWidth={2.2} aria-hidden="true" />
      <span className="min-w-0">{t('guestChat.loggedInAs', { name })}</span>
    </p>
  );
};

const SKELETON_STAFF = 'self-start rounded-[18px_18px_18px_6px] border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/40';
const SKELETON_GUEST = 'self-end rounded-[18px_18px_6px_18px] bg-blue-100 dark:bg-blue-950/60';
const BAR_STAFF = 'block h-3 rounded-md bg-green-100 dark:bg-green-900/60';
const BAR_GUEST = 'block h-3 rounded-md bg-blue-200 dark:bg-blue-900/70';

/** Caricamento: la scritta e tre bolle che pulsano (ferme con "riduci movimento"). */
export const ChatLoadingSkeleton: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div role="status" className="flex flex-col gap-3">
      <p className="m-0 mb-1.5 inline-flex items-center gap-2 self-center text-[13px] font-semibold text-nva-muted dark:text-stone-400">
        <Loader2 className="h-[15px] w-[15px] motion-safe:animate-spin" strokeWidth={2.6} aria-hidden="true" />
        {t('guestChat.loading')}
      </p>
      <div aria-hidden="true" className={cn('flex w-[68%] flex-col gap-2 p-3 motion-safe:animate-pulse', SKELETON_STAFF)}>
        <span className={cn(BAR_STAFF, 'h-2.5 w-[34%]')} />
        <span className={cn(BAR_STAFF, 'w-full')} />
        <span className={cn(BAR_STAFF, 'w-[78%]')} />
      </div>
      <div aria-hidden="true" className={cn('flex w-[56%] flex-col gap-2 p-3 motion-safe:animate-pulse [animation-delay:200ms]', SKELETON_GUEST)}>
        <span className={cn(BAR_GUEST, 'w-full')} />
        <span className={cn(BAR_GUEST, 'w-[60%]')} />
      </div>
      <div aria-hidden="true" className={cn('flex w-[74%] flex-col gap-2 p-3 motion-safe:animate-pulse [animation-delay:400ms]', SKELETON_STAFF)}>
        <span className={cn(BAR_STAFF, 'h-2.5 w-[30%]')} />
        <span className={cn(BAR_STAFF, 'w-full')} />
        <span className={cn(BAR_STAFF, 'w-[92%]')} />
        <span className={cn(BAR_STAFF, 'w-[48%]')} />
      </div>
    </div>
  );
};

/** Prima volta, nessun messaggio: il benvenuto della reception (testi della Home). */
export const ChatEmptyWelcome: React.FC<{ guestName: string | null }> = ({ guestName }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2.5 px-3 text-center">
      <span aria-hidden="true" className="flex h-[76px] w-[76px] items-center justify-center rounded-full border border-nva-edge bg-nva-card shadow-[0_8px_22px_rgba(42,38,32,.08)] dark:border-stone-700 dark:bg-stone-900 dark:shadow-none">
        <img src={CHAT_EMBLEM_SRC} alt="" width={60} height={60} className="block" decoding="async" />
      </span>
      <span aria-hidden="true" className="mt-1 block h-0.5 w-9 bg-nva-gold" />
      <p className="m-0 text-[11px] font-bold uppercase tracking-eyebrow text-nva-gold-text dark:text-nva-gold-dark">
        {t('home.quick.reception')}
      </p>
      <h2 className="m-0 font-display text-[24px] font-bold leading-[1.15] text-nva-espresso dark:text-stone-100">
        {t('home.reception.title')}
      </h2>
      <p className="m-0 text-[15px] leading-[1.45] text-nva-muted dark:text-stone-300">{t('home.reception.body')}</p>
      <p className="m-0 mt-1.5 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-blue-700 dark:text-blue-300">
        {t('guestChat.sendUsMessage')}
        <ArrowDown className="h-[15px] w-[15px] flex-shrink-0" strokeWidth={2.4} aria-hidden="true" />
      </p>
      {guestName ? <ConnectedAs name={guestName} className="mt-3.5" /> : null}
    </div>
  );
};

const CARD = 'overflow-hidden rounded-[20px] border bg-nva-card dark:border-stone-800 dark:bg-stone-900';

/** Caricamento fallito (senza messaggi da mostrare): la card con RIPROVA. */
export const ChatLoadError: React.FC<{ onRetry: () => void }> = ({ onRetry }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-1 flex-col justify-center">
      <section role="alert" className={cn(CARD, 'border-[#F3CFCF] shadow-[0_2px_12px_rgba(185,28,28,.06)] dark:shadow-none')}>
        <div aria-hidden="true" className="h-[5px] bg-gradient-to-r from-red-500 to-red-700" />
        <div className="flex flex-col items-center gap-2.5 px-4 pb-4 pt-[18px] text-center">
          <span aria-hidden="true" className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
            <AlertCircle className="h-6 w-6" strokeWidth={2.2} />
          </span>
          <h2 className="m-0 font-display text-[21px] font-bold leading-[1.2] text-nva-espresso dark:text-stone-100">
            {t('guestChat.error')}
          </h2>
          <p className="m-0 text-[14px] leading-[1.45] text-nva-muted dark:text-stone-300">{t('guestChat.errorHint')}</p>
          <button
            type="button"
            onClick={onRetry}
            className={cn(
              SERVICE_BUTTON,
              'mt-1 w-full border-[1.5px] border-nva-foot bg-nva-card text-nva-espresso',
              'dark:border-stone-600 dark:bg-stone-900 dark:text-stone-100',
            )}
          >
            <RotateCw className="h-[17px] w-[17px]" strokeWidth={2.3} aria-hidden="true" />
            {t('guestChat.tryAgain')}
          </button>
        </div>
      </section>
    </div>
  );
};

/** Sessione scaduta: la chat resta aperta e chiede di accedere di nuovo. */
export const ChatSessionExpired: React.FC<{ onRelogin: () => void }> = ({ onRelogin }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-1 flex-col justify-center">
      <section role="alert" className={cn(CARD, 'border-[#DCE6F2] shadow-[0_2px_12px_rgba(37,99,235,.07)] dark:shadow-none')}>
        <div aria-hidden="true" className="h-[5px] bg-gradient-to-br from-blue-600 to-indigo-600" />
        <div className="flex flex-col items-center gap-2.5 px-4 pb-4 pt-[18px] text-center">
          <span aria-hidden="true" className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
            <Lock className="h-[23px] w-[23px]" strokeWidth={2.1} />
          </span>
          <h2 className="m-0 font-display text-[22px] font-bold leading-[1.2] text-nva-espresso dark:text-stone-100">
            {t('guestChat.expired.title')}
          </h2>
          <p className="m-0 text-[14.5px] leading-[1.45] text-nva-muted dark:text-stone-300">{t('guestChat.expired.body')}</p>
          <button
            type="button"
            onClick={onRelogin}
            className={cn(SERVICE_BUTTON, 'mt-1 w-full bg-gradient-to-br from-blue-600 to-indigo-600 shadow-[0_8px_18px_rgba(37,99,235,.25)] dark:shadow-none')}
          >
            <LogIn className="h-[17px] w-[17px]" strokeWidth={2.2} aria-hidden="true" />
            {t('guestChat.expired.cta')}
          </button>
        </div>
      </section>
    </div>
  );
};

/** Senza rete: l'avviso fermo sotto la testata. */
export const ChatOfflineBanner: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="flex flex-shrink-0 items-start gap-2.5 border-b border-amber-200 bg-amber-50 px-4 py-[11px] dark:border-amber-900/60 dark:bg-amber-950/40"
    >
      <WifiOff className="mt-px h-[18px] w-[18px] flex-shrink-0 text-amber-700 dark:text-amber-300" strokeWidth={2.2} aria-hidden="true" />
      <p className="m-0 text-[13.5px] leading-[1.4] text-amber-800 dark:text-amber-200">
        <strong className="font-bold text-amber-900 dark:text-amber-100">{t('guestChat.offline.title')}</strong>{' '}
        {t('guestChat.offline.body')}
      </p>
    </div>
  );
};
